// Recommendation Engine
// This module handles game recommendations based on user play history and preferences

// Utility function to shuffle an array using Fisher-Yates algorithm
const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

// Get played games from localStorage with duration-based categorization
export const getPlayedGames = () => {
  try {
    const rawPlayedGames = JSON.parse(localStorage.getItem('playedGames') || '[]');
    
    // Process the raw data to combine multiple plays of the same game
    const gamePlayMap = {};
    
    rawPlayedGames.forEach(play => {
      if (!play.gameId) return; // Skip entries without gameId
      
      const gameId = play.gameId;
      
      // If this game hasn't been processed yet, initialize it
      if (!gamePlayMap[gameId]) {
        gamePlayMap[gameId] = {
          ...play,
          totalTimeSpentSeconds: play.timeSpentSeconds || 0,
          playCount: 1,
          durationWeight: 0,
          consideredForPreferences: (play.timeSpentSeconds || 0) >= 30
        };
      } else {
        // Update existing entry
        gamePlayMap[gameId].totalTimeSpentSeconds += (play.timeSpentSeconds || 0);
        gamePlayMap[gameId].playCount += 1;
        
        // Use the most recent play time
        if (new Date(play.playTime) > new Date(gamePlayMap[gameId].playTime)) {
          gamePlayMap[gameId].playTime = play.playTime;
        }
        
        // If any play session is >= 30 seconds, consider it for preferences
        if ((play.timeSpentSeconds || 0) >= 30) {
          gamePlayMap[gameId].consideredForPreferences = true;
        }
      }
    });
    
    // Convert the map back to an array and calculate duration weights
    const processedPlays = Object.values(gamePlayMap).map(play => {
      const timeSpent = play.totalTimeSpentSeconds || 0;
      
      // Calculate duration weight based on time spent
      let durationWeight = 0.0;
      
      if (timeSpent >= 3600) {
        durationWeight = 3.0;
      } else if (timeSpent >= 600) {
        durationWeight = 2.0;
      } else if (timeSpent >= 180) {
        durationWeight = 1.2;
      } else if (timeSpent >= 30) {
        durationWeight = 0.5;
      }
      
      return {
        ...play,
        durationWeight: play.consideredForPreferences ? durationWeight : 0,
        durationCategory: timeSpent < 30 ? "brief" : 
                         timeSpent < 180 ? "short" : 
                         timeSpent < 600 ? "medium" : 
                         timeSpent < 3600 ? "long" : "extensive",
        consideredForPreferences: play.consideredForPreferences
      };
    });
    
    return processedPlays;
  } catch (error) {
    console.error('Error reading played games:', error);
    return [];
  }
};

// Analyze user preferences based on play history
export const analyzePreferences = (playedGames) => {
  const platformCounts = {};
  const tagCounts = {};
  const yearCounts = {};
  let totalWeightedPlays = 0;
  let totalPlays = 0;
  let consideredPlays = 0;
  
  // Track duration categories for debugging
  const durationCategories = {
    brief: 0,
    short: 0,
    medium: 0,
    long: 0,
    extensive: 0
  };

  playedGames.forEach(play => {
    const durationWeight = play.durationWeight || 0;
    
    // Count duration categories
    if (play.durationCategory) {
      durationCategories[play.durationCategory] = (durationCategories[play.durationCategory] || 0) + 1;
    }
    
    // Track total raw play counts
    totalPlays++;
    
    // Only consider games with sufficient play time for preferences
    if (play.consideredForPreferences) {
      // Count platform occurrences with duration weight
      platformCounts[play.platformId] = (platformCounts[play.platformId] || 0) + durationWeight;
      
      // Track total weighted and considered plays
      totalWeightedPlays += durationWeight;
      consideredPlays++;

      // Count tag occurrences with duration weight
      if (Array.isArray(play.tags)) {
        play.tags.forEach(tag => {
          const tagId = typeof tag === 'object' ? tag.id : tag;
          tagCounts[tagId] = (tagCounts[tagId] || 0) + durationWeight;
        });
      }
      
      // Count year occurrences with duration weight
      if (play.year && !isNaN(parseInt(play.year))) {
        const year = parseInt(play.year);
        yearCounts[year] = (yearCounts[year] || 0) + durationWeight;
      }
    }
  });

  // Sort platforms and tags by frequency
  const sortedPlatformEntries = Object.entries(platformCounts)
    .sort(([,a], [,b]) => b - a)
    .map(([id, count]) => ({
      id: parseInt(id),
      count,
      percentage: ((count / totalWeightedPlays) * 100).toFixed(1)
    }));

  const sortedTagEntries = Object.entries(tagCounts)
    .sort(([,a], [,b]) => b - a)
    .map(([id, count]) => ({
      id: parseInt(id),
      count,
      percentage: ((count / totalWeightedPlays) * 100).toFixed(1)
    }));

  // Create a smoothed distribution of year preferences (1970-2025)
  const yearScores = {};
  
  // Initialize all years with zero count
  for (let year = 1970; year <= 2025; year++) {
    yearScores[year] = 0;
  }
  
  // Use played years as initial values
  Object.entries(yearCounts).forEach(([year, count]) => {
    yearScores[year] = count;
  });
  
  // Apply Gaussian smoothing to spread the influence of each played year
  const allYears = Array.from({ length: 2025 - 1970 + 1 }, (_, i) => 1970 + i);
  const smoothedYearScores = {};
  
  // Initialize all smoothed scores to 0
  allYears.forEach(year => {
    smoothedYearScores[year] = 0;
  });
  
  // For each year with plays, apply smoothing
  Object.entries(yearCounts).forEach(([playedYear, count]) => {
    const centerYear = parseInt(playedYear);
    // Apply smoothing to nearby years (Gaussian distribution)
    allYears.forEach(year => {
      const distance = Math.abs(year - centerYear);
      const sigma = 3; // Controls the width of the bell curve
      const decay = Math.exp(-(distance * distance) / (2 * sigma * sigma));
      smoothedYearScores[year] += count * decay;
    });
  });
  
  // Normalize the scores to percentages
  const maxScore = Math.max(...Object.values(smoothedYearScores), 1);
  const sortedYearEntries = Object.entries(smoothedYearScores)
    .map(([year, score]) => ({
      year: parseInt(year),
      score,
      normalizedScore: (score / maxScore * 100),
      percentage: ((score / maxScore) * 100).toFixed(1)
    }))
    .sort((a, b) => b.score - a.score);

  return {
    preferredPlatforms: sortedPlatformEntries,
    preferredTags: sortedTagEntries,
    preferredYears: sortedYearEntries,
    yearCounts,
    smoothedYearScores,
    totalPlays,
    totalWeightedPlays,
    consideredPlays,
    durationCategories
  };
};

// Return fixed weights for recommendation scoring
export const getFixedWeights = () => {
  // ⚠️ TEMPORARY WEIGHT ADJUSTMENT ⚠️
  // This is a temporary configuration for testing purposes
  // Year, tag, and quality weights are set to 0, freshness now acts as a scaling factor (0-1)
  // TODO: Revert to original weights after testing period
  return {
    year: 0.0,        // TEMP: Disabled year-based recommendations
    tags: 0.0,        // TEMP: Disabled tag-based recommendations  
    quality: 0.0,     // TEMP: Disabled quality-based recommendations
    freshness: 1.0,   // TEMP: Not used in weighted sum - acts as scaling factor
    random: 1.0       // TEMP: Only randomness used in base score
  };
};

// Calculate quality score for a game
export const calculateQualityScore = (game, maxViews) => {
  if (!game.views) return 0;
  
  // Directly scale based on the maximum views
  const directScale = (game.views / maxViews) * 100;
  
  // Logarithmic scaling for better distribution
  const logScale = (Math.log10(game.views) / Math.log10(maxViews)) * 100;
  
  // Blend both approaches - 40% direct scaling, 60% logarithmic scaling
  return (0.4 * directScale) + (0.6 * logScale);
};

// Calculate freshness score (higher for games with fewer plays and less time spent)
export const calculateFreshnessScore = (game, playCountMap, playDurationMap, playDurationCategoryMap, viewCountMap = {}) => {
  if (!game.id) return 1.0; // Maximum freshness scaling factor if no game ID
  
  const gameId = game.id;
  const playCount = playCountMap[gameId] || 0;
  // eslint-disable-next-line no-unused-vars
  const playDuration = playDurationMap[gameId] || 0;
  // eslint-disable-next-line no-unused-vars
  const viewCount = viewCountMap[gameId] || 0;
  const durationCategory = playDurationCategoryMap[gameId] || 'brief';
  
  // ⚠️ TESTING: PLAY-ONLY FRESHNESS PENALTIES ⚠️
  // Only playing impacts freshness, viewing is ignored
  // TODO: Revert to original freshness calculation after testing
  
  // TESTING: Extremely aggressive decay for any play activity only
  let freshnessFactor = Math.pow(0.1, Math.min(playCount, 2)); // Much more aggressive than original 0.5
  
  // TESTING: Ignore view count completely - only playing matters
  // freshnessFactor *= Math.pow(0.3, Math.min(viewCount, 5)); // DISABLED - viewing no longer affects freshness
  
  // TESTING: Extremely harsh penalties based on play duration
  switch(durationCategory) {
    case 'brief': // 0-30s
      freshnessFactor *= 0.2; // TESTING: 80% decay (was 15%)
      break;
    case 'short': // 30s-3m
      freshnessFactor *= 0.05; // TESTING: 95% decay (was 30%)
      break;
    case 'medium': // 3m-10m
      freshnessFactor *= 0.02; // TESTING: 98% decay (was 50%)
      break;
    case 'long': // 10m-60m
      freshnessFactor *= 0.005; // TESTING: 99.5% decay (was 70%)
      break;
    case 'extensive': // 60m+
      freshnessFactor *= 0.001; // TESTING: 99.9% decay (was 85%)
      break;
    default:
      // No additional penalty for unknown categories
      break;
  }
  
  // TESTING: Return as 0-1 scaling factor instead of 0-100 percentage
  return freshnessFactor; // Returns 0-1 to scale other scores
};

// Generate a truly random number (0-1)
export const generateTrueRandom = () => {
  return Math.random();
};

// Get recommended games based on user preferences
export const getRecommendedGames = (allGames, preferences, platformMap) => {
  // Find the maximum view count across all games
  const maxViews = Math.max(...allGames.map(game => game.views || 0));
  
  // Build view counts map from localStorage
  const rawViewed = JSON.parse(localStorage.getItem('viewedGames') || '[]');
  const viewCountMap = {};
  rawViewed.forEach(id => {
    if (!id) return;
    viewCountMap[id] = (viewCountMap[id] || 0) + 1;
  });

  // Get played game history
  const playedGames = getPlayedGames();
  
  // Create maps for play counts and play durations
  const playCountMap = {};
  const playDurationMap = {};
  const playDurationCategoryMap = {};
  
  playedGames.forEach(play => {
    if (play.gameId) {
      playCountMap[play.gameId] = play.playCount || 1;
      playDurationMap[play.gameId] = play.totalTimeSpentSeconds || 0;
      playDurationCategoryMap[play.gameId] = play.durationCategory || 'brief';
    }
  });
  
  const weights = getFixedWeights();

  // Score each game
  const scoredGames = allGames.map(game => {
    let yearScore = 0;
    let tagScore = 0;
    let matchedTags = [];
    
    // Year score - use platform's defaultyear if game year is missing
    let gameYear = null;
    if (game.year && !isNaN(parseInt(game.year))) {
      gameYear = parseInt(game.year);
    } else if (game.platformInfo && game.platformInfo.defaultyear && !isNaN(parseInt(game.platformInfo.defaultyear))) {
      gameYear = parseInt(game.platformInfo.defaultyear);
    }
    
    // Calculate year score if we have a valid year
    if (gameYear) {
      if (preferences.smoothedYearScores && preferences.smoothedYearScores[gameYear]) {
        const yearValue = preferences.smoothedYearScores[gameYear];
        const maxScore = Math.max(...Object.values(preferences.smoothedYearScores), 1);
        yearScore = (yearValue / maxScore) * 100; // Scale to 0-100
      }
    }

    // Tags score
    if (game.tags && game.tags.length > 0) {
      const tagMatches = game.tags
        .map(gameTag => {
          const tagMatch = preferences.preferredTags.find(t => t.id === gameTag.id);
          if (tagMatch) {
            matchedTags.push({
              name: gameTag.name,
              percentage: parseFloat(tagMatch.percentage)
            });
            return parseFloat(tagMatch.percentage);
          }
          return 0;
        })
        .filter(score => score > 0);

      if (tagMatches.length > 0) {
        const matchCount = tagMatches.length;
        const totalTagPercentage = tagMatches.reduce((a, b) => a + b, 0);
        
        // Simplified and more balanced tag scoring
        const percentageScore = Math.min(75, totalTagPercentage); // Cap at 75%
        const countScore = Math.min(25, matchCount * 10); // Cap multiple matches bonus at 25%
        
        tagScore = Math.min(100, percentageScore + countScore);
      }
    }

    // Calculate scores
    const qualityScore = calculateQualityScore(game, maxViews);
    const freshnessScalingFactor = calculateFreshnessScore(game, playCountMap, playDurationMap, playDurationCategoryMap, viewCountMap);
    const randomScore = generateTrueRandom() * 100;

    // TESTING: Calculate base weighted score (without freshness)
    const baseScore = (
      (yearScore * weights.year) +
      (tagScore * weights.tags) +
      (qualityScore * weights.quality) +
      (randomScore * weights.random)
    );
    
    // TESTING: Apply freshness as scaling factor (0-1) to the combined base score
    let finalScore = baseScore * freshnessScalingFactor;
    
    // TESTING: Apply platform-specific scaling for platform ID 7 (89%)
    const platformScalingFactor = game.platform === 7 ? 0.89 : 1.0;
    finalScore = finalScore * platformScalingFactor;

    // Get play duration info for debugging
    const playDuration = playDurationMap[game.id] || 0;
    const durationCategory = playDurationCategoryMap[game.id] || null;
    return {
      game,
      score: finalScore,
      yearScore, 
      tagScore,
      qualityScore,
      freshnessScore: freshnessScalingFactor * 100, // Convert back to 0-100 for display
      randomScore,
      baseScore, // TESTING: Show base score before freshness scaling
      freshnessScalingFactor, // TESTING: Show the 0-1 scaling factor
      platformScalingFactor, // TESTING: Show platform scaling factor (0.6 for platform 7)
      matchedTags,
      matchedTagCount: matchedTags.length,
      weights,
      playCount: playCountMap[game.id] || 0,
      playDuration,
      durationCategory
    };
  });

  // Sort by score and get top 15
  const sortedGames = scoredGames
    .sort((a, b) => b.score - a.score)
    .slice(0, 15);
    
  // Shuffle the top 15 games while preserving their scores
  // This ensures each batch of 15 games with top scores is shuffled before being displayed
  const shuffledGames = shuffleArray(sortedGames);

  // Prepare debug info
  const debug = {
    message: "Recommendations based on play history and play duration (shuffled)",
    platforms: preferences.preferredPlatforms.map(p => {
      const platform = platformMap[p.id];
      return {
        name: platform ? (platform.namechn || platform.nameeng) : `Platform ${p.id}`,
        percentage: p.percentage + '%',
        count: p.count
      };
    }),
    tags: preferences.preferredTags.map(t => {
      const gameWithTag = allGames.find(g => g.tags?.some(tag => tag.id === t.id));
      const tagInfo = gameWithTag?.tags?.find(tag => tag.id === t.id);
      return {
        name: tagInfo?.name || 'Unknown',
        percentage: t.percentage + '%',
        count: t.count
      };
    }),
    yearDistribution: {
      topYears: preferences.preferredYears.slice(0, 10).map(y => ({
        year: y.year,
        percentage: y.percentage + '%',
        score: y.score.toFixed(2)
      })),
      fullDistribution: Array.from({ length: 2025 - 1970 + 1 }, (_, i) => {
        const year = 1970 + i;
        return {
          year,
          score: preferences.smoothedYearScores[year].toFixed(2),
          normalizedScore: ((preferences.smoothedYearScores[year] / Math.max(...Object.values(preferences.smoothedYearScores), 1)) * 100).toFixed(1)
        };
      }),
      rawCounts: Object.entries(preferences.yearCounts || {}).map(([year, count]) => ({
        year: parseInt(year),
        count
      })).sort((a, b) => a.year - b.year)
    },
    playedGames: {
      totalGamesPlayed: Object.keys(playCountMap).length,
      durationCategories: preferences.durationCategories || {},
      totalPlays: preferences.totalPlays,
      totalWeightedPlays: preferences.totalWeightedPlays?.toFixed(1) || preferences.totalPlays,
      playCountsByGame: Object.entries(playCountMap)
        .map(([gameId, count]) => {
          const game = allGames.find(g => g.id === parseInt(gameId));
          const duration = playDurationMap[gameId] || 0;
          const durationCategory = playDurationCategoryMap[gameId] || 'brief';
          
          let formattedDuration = '';
          if (duration >= 3600) {
            formattedDuration = `${Math.floor(duration / 3600)}h ${Math.floor((duration % 3600) / 60)}m`;
          } else if (duration >= 60) {
            formattedDuration = `${Math.floor(duration / 60)}m ${duration % 60}s`;
          } else {
            formattedDuration = `${duration}s`;
          }
          
          return {
            gameId: parseInt(gameId),
            title: game ? (game.titlechn || game.titleeng) : `Game ${gameId}`,
            playCount: count,
            duration: duration,
            formattedDuration: formattedDuration,
            durationCategory: durationCategory
          };
        })
        .sort((a, b) => b.duration - a.duration)
        .slice(0, 10)
    },
    weights,
    consideredPlays: preferences.consideredPlays,
    detailedScores: sortedGames.map(item => {
      // Determine if using platform default year
      const game = item.game;
      const usingPlatformYear = !game.year && game.platformInfo && game.platformInfo.defaultyear;
      const yearDisplay = usingPlatformYear ? 
        `${game.platformInfo.defaultyear} (平台默认)` : 
        (game.year || '未知');
        
      return {
        ...item,
        yearDisplay,
        usingPlatformYear,
        matchedTagsInfo: item.matchedTags.map(t => `${t.name} (${t.percentage.toFixed(1)}%)`).join(', '),
        weightedScores: {
          year: (item.yearScore * weights.year).toFixed(1),
          tags: (item.tagScore * weights.tags).toFixed(1),
          quality: (item.qualityScore * weights.quality).toFixed(1),
          random: (item.randomScore * weights.random).toFixed(1),
          baseScore: item.baseScore.toFixed(1), // TESTING: Base score before freshness scaling
          freshnessScaling: item.freshnessScalingFactor.toFixed(3), // TESTING: 0-1 scaling factor
          platformScaling: item.platformScalingFactor.toFixed(1), // TESTING: Platform scaling factor
          finalScore: item.score.toFixed(1) // TESTING: Final score after all scaling
        },
        viewCount: viewCountMap[game.id] || 0,
        playInfo: item.playCount > 0 ? 
          `${item.playCount}次 / ${item.durationCategory || 'brief'} (${Math.floor(item.playDuration / 60)}m ${item.playDuration % 60}s)${item.durationCategory === 'brief' ? ' - 不计入偏好' : ''}` : 
          '未玩过'
      };
    })
  };

  return { games: shuffledGames.map(g => g.game), debug };
};

// Refresh recommendations with a new random seed
export const refreshRecommendations = (allGames, platforms) => {
  const playedGames = getPlayedGames();
  const preferences = analyzePreferences(playedGames);
  const { games, debug } = getRecommendedGames(allGames, preferences, platforms);
  
  // Log score distribution for verification
  if (debug && debug.detailedScores && debug.detailedScores.length > 0) {
    const scores = debug.detailedScores;
    const avgYear = scores.reduce((sum, item) => sum + item.yearScore, 0) / scores.length;
    const avgTags = scores.reduce((sum, item) => sum + item.tagScore, 0) / scores.length;
    const avgQuality = scores.reduce((sum, item) => sum + item.qualityScore, 0) / scores.length;
    const avgFreshness = scores.reduce((sum, item) => sum + item.freshnessScore, 0) / scores.length;
    const avgRandom = scores.reduce((sum, item) => sum + item.randomScore, 0) / scores.length;
    
    console.log("Score Distribution Average - Year:", avgYear.toFixed(1), 
                "Tags:", avgTags.toFixed(1), 
                "Quality:", avgQuality.toFixed(1),
                "Freshness:", avgFreshness.toFixed(1),
                "Random:", avgRandom.toFixed(1));
  }
  
  return { games, debug };
};